// getExpertDescription.js
// Helper function to generate expert description from document content and selected text

import {callLLM} from '../../../utils/llmApi';

/**
 * Extract plain text content from the document
 * @param {Object} doc - ProseMirror document
 * @returns {Array<string>} Array of text content strings
 */
function extractDocumentContent(doc) {
    const contentArray = [];

    doc.descendants((node) => {
        if (node.isText && node.text) {
            contentArray.push(node.text);
        }
    });

    return contentArray;
}

/**
 * Generate expert description using LLM based on document content and selected text
 * @param {Object} state - ProseMirror editor state
 * @param {string} selectedText - The text selected by the user
 * @returns {Promise<string>} Expert description
 */
export async function getExpertDescription(state, selectedText) {
    try {
        // Extract document content (only text nodes)
        const documentContent = extractDocumentContent(state.doc);

        // Build the prompt for the LLM
        const systemPrompt = `You are an expert at identifying the right expertise needed for specific topics in documents.

Your task is to analyze a document and a specific selected passage, then provide a direct, concise description of the expert profile needed.

Write the description in a straightforward manner as if filling in: "An expert who is ___"

Focus on:
- The domain expertise required (e.g., "a geopolitical analyst specializing in...")
- Relevant specialized knowledge areas
- Professional background or credentials

DO NOT start with phrases like "The ideal expert" or "You need an expert" - just describe the expert directly.
Return ONLY the expert description text, no additional formatting or explanation.`;

        const userPrompt = `
        {
            content: ${documentContent.slice(0, 100).join(' ')}${documentContent.length > 100 ? '...' : ''},
            selectedText: ${selectedText}
            }
        `;

        const messages = [
            {role: 'system', content: systemPrompt},
            {role: 'user', content: userPrompt}
        ];

        const model = 'gpt-4o-mini';
        const options = {
            temperature: 0.7,
            max_tokens: 300
        };

        console.log('Calling LLM for expert description...');
        const response = await callLLM(messages, model, options);

        // Extract the content from the response
        const content = response.choices?.[0]?.message?.content || response.content;

        if (!content) {
            throw new Error('No content in LLM response');
        }

        return content.trim();
    } catch (error) {
        console.error('Error getting expert description:', error);
        throw error;
    }
}
